import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Fingerprint, Loader2, Lock, ShieldCheck, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from './Button';
import api from '../services/api';
import type { EmployerLocation, EmployerDepartment } from './EmployerOrgStructuur';
import { getLijnmanagers, openPoortwachterCase } from '../services/lopendeTrajectenApi';
import { medewerkerHash, naamGrendelBeschikbaar, normaliseerContact, pseudoniemUitHash } from '../utils/naamGrendel';

/**
 * De 60-seconden Ziekmeldings-Wizard (Compliance by Code).
 *
 * Vier velden; bij [Bevestig Ziekmelding] splitst de browser de invoer:
 *   Stroom A  naam + contact → AES-GCM lokaal (LocalSecureStorage), nooit naar de server
 *   Stroom B  SHA-256(employer_id | contact) → de enige identifier in de database;
 *             het pseudoniem MDW-xxxxx-X wordt daaruit afgeleid en live getoond.
 */

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';
const apiMessage = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback;

export interface ZiekmeldingWizardProps {
  employerId: string;
  onBewaarNaam: (hash: string, m: { naam: string; contact: string }) => Promise<void>;
  onKlaar: () => void;
  onSluit: () => void;
}

export const ZiekmeldingWizard: React.FC<ZiekmeldingWizardProps> = ({ employerId, onBewaarNaam, onKlaar, onSluit }) => {
  const { t } = useTranslation('employer');
  const { data: locations = [] } = useQuery({ queryKey: ['employer-locations'], queryFn: async () => (await api.get('/employer/locations')).data?.data as EmployerLocation[] });
  const { data: departments = [] } = useQuery({ queryKey: ['employer-departments'], queryFn: async () => (await api.get('/employer/departments')).data?.data as EmployerDepartment[] });
  const { data: lijnmanagers = [] } = useQuery({ queryKey: ['lopende-trajecten', 'lijnmanagers'], queryFn: getLijnmanagers });

  const [vorm, setVorm] = useState({
    naam: '',
    contact: '',
    ziekmelding_datum: new Date().toISOString().slice(0, 10),
    location_id: '',
    department_id: '',
    lijnmanager_user_id: '',
    notitie: '',
  });
  const [hash, setHash] = useState<string | null>(null);
  const grendel = naamGrendelBeschikbaar();

  // Live preview van Stroom B: de hash (en dus de code) verandert mee met het contactgegeven
  useEffect(() => {
    const contact = vorm.contact.trim();
    if (!grendel || contact.length < 5) {
      setHash(null);
      return;
    }
    let actief = true;
    medewerkerHash(employerId, contact).then((h) => actief && setHash(h));
    return () => {
      actief = false;
    };
  }, [vorm.contact, employerId, grendel]);

  const code = useMemo(() => (hash ? pseudoniemUitHash(hash) : null), [hash]);
  const afdelingen = vorm.location_id ? departments.filter((d) => !d.location_id || d.location_id === vorm.location_id) : departments;
  const contactGeldig = /\S+@\S+\.\S+/.test(vorm.contact) || /^\+?[\d\s\-()]{10,}$/.test(vorm.contact.trim());
  const compleet = vorm.naam.trim().length >= 2 && contactGeldig && !!vorm.ziekmelding_datum && !!hash;

  const m = useMutation({
    mutationFn: async () => {
      if (!hash) throw new Error(t('ziekmeldingWizard.hashNietBerekend'));
      // Stroom A: lokaal, vóór de server-call — zonder lokale sleutel gaat er niets naar buiten
      await onBewaarNaam(hash, { naam: vorm.naam.trim(), contact: normaliseerContact(vorm.contact) });
      // Stroom B: alleen de hash + organisatorische context
      return openPoortwachterCase({
        ziekmelding_datum: vorm.ziekmelding_datum,
        location_id: vorm.location_id || null,
        department_id: vorm.department_id || null,
        lijnmanager_user_id: vorm.lijnmanager_user_id || null,
        medewerker_hash: hash,
        notitie: vorm.notitie || null,
      });
    },
    onSuccess: (c) => {
      toast.success(
        c.eerdere_cases
          ? t('ziekmeldingWizard.toastGeopendMetEerdere', { caseCode: c.case_code, pseudoniem: c.pseudoniem, aantal: c.eerdere_cases })
          : t('ziekmeldingWizard.toastGeopend', { caseCode: c.case_code, pseudoniem: c.pseudoniem })
      );
      onKlaar();
    },
    onError: (e: any) => toast.error(apiMessage(e, t('ziekmeldingWizard.fout'))),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" role="dialog" aria-modal="true" aria-label={t('ziekmeldingWizard.ariaLabel')}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Fingerprint className="w-4 h-4 text-red-600" /> {t('ziekmeldingWizard.titel')}
          </h3>
          <button type="button" onClick={onSluit} aria-label={t('ziekmeldingWizard.sluiten')} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form
          className="p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (compleet && !m.isPending) m.mutate();
          }}
        >
          {!grendel && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-900">
              {t('ziekmeldingWizard.geenGrendel')}
            </div>
          )}

          {/* 1. Naam & contact */}
          <fieldset className="p-3 rounded-lg border border-gray-200 bg-gray-50">
            <legend className="px-1 text-xs font-semibold text-gray-700">{t('ziekmeldingWizard.stap1')}</legend>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className={labelClass} htmlFor="zw-naam">{t('ziekmeldingWizard.naamLabel')}</label>
                <input id="zw-naam" className={inputClass} placeholder={t('ziekmeldingWizard.naamPlaceholder')} autoComplete="off" value={vorm.naam} onChange={(e) => setVorm({ ...vorm, naam: e.target.value })} />
                <p className="mt-1 text-[11px] text-gray-500 flex items-center gap-1"><Lock className="w-3 h-3" /> {t('ziekmeldingWizard.naamHint')}</p>
              </div>
              <div>
                <label className={labelClass} htmlFor="zw-contact">{t('ziekmeldingWizard.contactLabel')}</label>
                <input id="zw-contact" className={inputClass} placeholder={t('ziekmeldingWizard.contactPlaceholder')} autoComplete="off" value={vorm.contact} onChange={(e) => setVorm({ ...vorm, contact: e.target.value })} />
                <p className="mt-1 text-[11px] text-gray-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {t('ziekmeldingWizard.contactHint')}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-gray-500">{t('ziekmeldingWizard.codeLabel')}</span>
              <span className={`font-mono font-semibold px-2 py-0.5 rounded ${code ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`}>{code ?? 'MDW-·····-·'}</span>
              {hash && <span className="font-mono text-[10px] text-gray-400 truncate" title={hash}>sha256 {hash.slice(0, 12)}…</span>}
            </div>
          </fieldset>

          {/* 2. Eerste ziektedag */}
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass} htmlFor="zw-datum">{t('ziekmeldingWizard.datumLabel')}</label>
              <input id="zw-datum" type="date" className={inputClass} value={vorm.ziekmelding_datum} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setVorm({ ...vorm, ziekmelding_datum: e.target.value })} />
              <p className="mt-1 text-[11px] text-gray-500">{t('ziekmeldingWizard.datumHint')}</p>
            </div>
            {/* 3. Toegangs-Scope */}
            <div>
              <label className={labelClass} htmlFor="zw-vestiging">{t('ziekmeldingWizard.vestigingLabel')}</label>
              <select id="zw-vestiging" className={inputClass} value={vorm.location_id} onChange={(e) => setVorm({ ...vorm, location_id: e.target.value, department_id: '' })}>
                <option value="">{t('ziekmeldingWizard.alleOnbekend')}</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.naam}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="zw-afdeling">{t('ziekmeldingWizard.afdelingLabel')}</label>
              <select id="zw-afdeling" className={inputClass} value={vorm.department_id} onChange={(e) => setVorm({ ...vorm, department_id: e.target.value })}>
                <option value="">{t('ziekmeldingWizard.alleOnbekend')}</option>
                {afdelingen.map((a) => <option key={a.id} value={a.id}>{a.naam}</option>)}
              </select>
            </div>
          </div>

          {/* 4. Lijnmanager */}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className={labelClass} htmlFor="zw-lm">{t('ziekmeldingWizard.lijnmanagerLabel')}</label>
              <select id="zw-lm" className={inputClass} value={vorm.lijnmanager_user_id} onChange={(e) => setVorm({ ...vorm, lijnmanager_user_id: e.target.value })}>
                <option value="">{t('ziekmeldingWizard.geenHoofdgebruiker')}</option>
                {lijnmanagers.map((l) => (
                  <option key={l.user_id} value={l.user_id}>{l.naam}{l.afdeling ? ` · ${l.afdeling}` : ''}{l.rol === 'hoofdgebruiker' ? ` (${t('ziekmeldingWizard.hoofdgebruiker')})` : ''}</option>
                ))}
              </select>
              <p className="mt-1 text-[11px] text-gray-500">{t('ziekmeldingWizard.lijnmanagerHint')}</p>
            </div>
            <div>
              <label className={labelClass} htmlFor="zw-notitie">{t('ziekmeldingWizard.notitieLabel')}</label>
              <input id="zw-notitie" className={inputClass} value={vorm.notitie} maxLength={2000} onChange={(e) => setVorm({ ...vorm, notitie: e.target.value })} placeholder={t('ziekmeldingWizard.notitiePlaceholder')} />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-3 border-t">
            <p className="text-xs text-gray-500">
              <Trans t={t} i18nKey="ziekmeldingWizard.geenMedischeInfo" components={{ u: <u /> }} />
            </p>
            <div className="flex gap-2 flex-shrink-0">
              <Button type="button" size="sm" variant="outline" onClick={onSluit}>{t('ziekmeldingWizard.annuleren')}</Button>
              <Button type="submit" size="sm" disabled={!compleet || m.isPending || !grendel}>
                {m.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : t('ziekmeldingWizard.bevestig')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
